import { useState, useEffect } from 'react';
import { X, Check, Palette, Plus, Edit2, Trash2 } from 'lucide-react';

export interface CatalogItem {
    id: string;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    isActive: boolean;
}

export interface CatalogManagerProps {
    title: string;
    description: string;
    icon?: React.ReactNode;
    accentColor: string; // e.g., 'emerald', 'violet', 'amber'
    items: CatalogItem[];
    onAdd: (item: Omit<CatalogItem, 'id' | 'isActive'>) => void;
    onUpdate: (id: string, data: Partial<CatalogItem>) => void;
    onDelete: (id: string) => void;
    showColorPicker?: boolean;
    placeholder?: string;
}

// Premium Color Palette - Vibrant & Distinct
const colorOptions = [
    { name: 'Red', value: '#EF4444', class: 'bg-red-500' },
    { name: 'Orange', value: '#F97316', class: 'bg-orange-500' },
    { name: 'Amber', value: '#F59E0B', class: 'bg-amber-500' },
    { name: 'Green', value: '#22C55E', class: 'bg-green-500' },
    { name: 'Emerald', value: '#10B981', class: 'bg-emerald-500' },
    { name: 'Teal', value: '#14B8A6', class: 'bg-teal-500' },
    { name: 'Cyan', value: '#06B6D4', class: 'bg-cyan-500' },
    { name: 'Blue', value: '#3B82F6', class: 'bg-blue-500' },
    { name: 'Indigo', value: '#6366F1', class: 'bg-indigo-500' },
    { name: 'Violet', value: '#8B5CF6', class: 'bg-violet-500' },
    { name: 'Purple', value: '#A855F7', class: 'bg-purple-500' },
    { name: 'Fuchsia', value: '#D946EF', class: 'bg-fuchsia-500' },
    { name: 'Pink', value: '#EC4899', class: 'bg-pink-500' },
    { name: 'Rose', value: '#F43F5E', class: 'bg-rose-500' },
];

const CatalogManager = ({
    title,
    description,
    icon,
    accentColor,
    items,
    onAdd,
    onUpdate,
    onDelete,
    showColorPicker = false,
    placeholder = 'Nuevo item...'
}: CatalogManagerProps) => {
    const [isAdding, setIsAdding] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);

    // Auto-select next available color logic
    const getNextAvailableColor = () => {
        const usedColors = items.map(item => item.color).filter(Boolean);
        const availableColor = colorOptions.find(c => !usedColors.includes(c.value));
        // If all colors are used, pick a random one to avoid direct duplicates if possible
        if (!availableColor) {
            return colorOptions[Math.floor(Math.random() * colorOptions.length)].value;
        }
        return availableColor.value;
    };

    const [newItem, setNewItem] = useState({
        name: '',
        description: '',
        color: getNextAvailableColor()
    });

    // Update newItem color when items change or when opening add form
    useEffect(() => {
        if (isAdding) {
            setNewItem(prev => ({ ...prev, color: getNextAvailableColor() }));
        }
    }, [items, isAdding]);

    const accentColorClasses: Record<string, { bg: string; bgLight: string; text: string; border: string; ring: string }> = {
        emerald: { bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'focus:ring-emerald-500' },
        violet: { bg: 'bg-violet-600', bgLight: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', ring: 'focus:ring-violet-500' },
        amber: { bg: 'bg-amber-600', bgLight: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', ring: 'focus:ring-amber-500' },
        blue: { bg: 'bg-blue-600', bgLight: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', ring: 'focus:ring-blue-500' },
        rose: { bg: 'bg-rose-600', bgLight: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', ring: 'focus:ring-rose-500' }
    };

    const colors = accentColorClasses[accentColor] || accentColorClasses.emerald;


    const handleAdd = () => {
        if (!newItem.name.trim()) return;
        onAdd({
            name: newItem.name.trim(),
            description: newItem.description.trim() || undefined,
            color: showColorPicker ? newItem.color : undefined
        });
        setNewItem({ name: '', description: '', color: getNextAvailableColor() });
        setIsAdding(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAdd();
        } else if (e.key === 'Escape') {
            setIsAdding(false);
            setNewItem({ name: '', description: '', color: getNextAvailableColor() });
        }
    };

    const handleEditClick = (item: CatalogItem) => {
        setEditingItem({ ...item });
        setEditModalOpen(true);
    };

    const handleSaveEdit = () => {
        if (!editingItem || !editingItem.name.trim()) return;
        onUpdate(editingItem.id, {
            name: editingItem.name.trim(),
            description: editingItem.description?.trim() || undefined,
            color: showColorPicker ? editingItem.color : undefined
        });
        setEditModalOpen(false);
        setEditingItem(null);
    };

    const handleCancelEdit = () => {
        setEditModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100/80 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
            {/* Header */}
            <div className={`px-6 py-5 ${colors.bgLight} border-b ${colors.border} flex items-center justify-between`}>
                <div className="flex items-center gap-4">
                    {icon && (
                        <div className={`w-12 h-12 rounded-xl ${colors.bg} text-white flex items-center justify-center shadow-lg shadow-${accentColor}-200`}>
                            {icon}
                        </div>
                    )}
                    <div>
                        <h3 className="font-bold text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500">{description}</p>
                    </div>
                </div>
                <span className={`text-sm font-semibold ${colors.text} px-3 py-1 rounded-full bg-white shadow-sm border ${colors.border}`}>
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
            </div>

            {/* Items List */}
            <div className="p-4">
                {items.length === 0 && !isAdding ? (
                    <div className="text-center py-10 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                        <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <Palette className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm mb-4">No hay elementos configurados</p>
                        <button
                            onClick={() => setIsAdding(true)}
                            className={`px-5 py-2.5 ${colors.bg} text-white text-sm font-medium rounded-xl hover:opacity-90 transition-all shadow-md hover:shadow-lg`}
                        >
                            Comenzar a agregar
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {items.map(item => (
                            <div
                                key={item.id}
                                className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/80 transition-all bg-white"
                            >
                                {/* Color indicator */}
                                {showColorPicker && item.color && (
                                    <div
                                        className="w-4 h-4 rounded-full ring-2 ring-white shadow-sm flex-shrink-0"
                                        style={{ backgroundColor: item.color }}
                                    />
                                )}

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{item.name}</p>
                                    {item.description && (
                                        <p className="text-xs text-gray-500 truncate">{item.description}</p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0 duration-200">
                                    <button
                                        onClick={() => handleEditClick(item)}
                                        className="p-2 hover:bg-gray-100 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                                        title="Editar"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(item.id)}
                                        className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                                        title="Eliminar"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Add New Item Form */}
                        {isAdding ? (
                            <div className={`p-5 rounded-2xl border-2 border-dashed ${colors.border} ${colors.bgLight} animate-in fade-in zoom-in-95 duration-200`}>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className={`text-sm font-semibold ${colors.text}`}>Nuevo Elemento</h4>
                                        <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            autoFocus
                                            value={newItem.name}
                                            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                            onKeyDown={handleKeyDown}
                                            placeholder={placeholder}
                                            className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm shadow-sm ${colors.ring} focus:ring-2 focus:border-transparent transition-all bg-white`}
                                        />

                                        <input
                                            type="text"
                                            value={newItem.description}
                                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                            placeholder="Descripción (opcional)"
                                            className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm shadow-sm ${colors.ring} focus:ring-2 focus:border-transparent transition-all bg-white`}
                                        />

                                        {showColorPicker && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Color Distintivo</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {colorOptions.map(color => (
                                                        <button
                                                            key={color.value}
                                                            type="button"
                                                            onClick={() => setNewItem({ ...newItem, color: color.value })}
                                                            className={`w-8 h-8 rounded-full transition-all duration-200 flex items-center justify-center ${newItem.color === color.value
                                                                ? 'ring-2 ring-offset-2 ring-gray-900 scale-110 shadow-md'
                                                                : 'hover:scale-110 hover:shadow-sm opacity-80 hover:opacity-100 hover:ring-1 hover:ring-gray-300'
                                                                }`}
                                                            style={{ backgroundColor: color.value }}
                                                            title={color.name}
                                                        >
                                                            {newItem.color === color.value && <Check className="w-4 h-4 text-white drop-shadow-md" strokeWidth={3} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={() => setIsAdding(false)}
                                            className="flex-1 px-4 py-2.5 border border-gray-200 bg-white text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleAdd}
                                            disabled={!newItem.name.trim()}
                                            className={`flex-1 px-4 py-2.5 ${colors.bg} text-white text-sm font-medium rounded-xl hover:brightness-110 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed`}
                                        >
                                            Agregar Item
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsAdding(true)}
                                className={`w-full py-3.5 border border-dashed ${colors.border} rounded-xl text-sm font-medium ${colors.text} hover:${colors.bgLight} hover:border-solid hover:shadow-sm transition-all flex items-center justify-center gap-2 group`}
                            >
                                <div className={`w-6 h-6 rounded-full ${colors.bgLight} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                    <Plus className="w-4 h-4" />
                                </div>
                                Agregar Nuevo Item
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Modal - Premium Glassmorphism */}
            {editModalOpen && editingItem && (
                <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-white/20 ring-1 ring-black/5">
                        {/* Modal Header */}
                        <div className={`px-6 py-4 bg-gradient-to-r ${accentColor === 'emerald' ? 'from-emerald-50 to-teal-50' : 'from-gray-50 to-white'} border-b border-gray-100 flex items-center justify-between`}>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <Edit2 className={`w-5 h-5 ${colors.text}`} />
                                Editar Item
                            </h3>
                            <button
                                onClick={handleCancelEdit}
                                className="p-2 hover:bg-white hover:shadow-sm rounded-full transition-all text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Nombre
                                </label>
                                <input
                                    type="text"
                                    value={editingItem.name}
                                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl ${colors.ring} focus:ring-2 focus:border-transparent transition-all bg-gray-50/50 focus:bg-white`}
                                    placeholder={placeholder}
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Descripción (opcional)
                                </label>
                                <input
                                    type="text"
                                    value={editingItem.description || ''}
                                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl ${colors.ring} focus:ring-2 focus:border-transparent transition-all bg-gray-50/50 focus:bg-white`}
                                    placeholder="Breve descripción"
                                />
                            </div>

                            {showColorPicker && (
                                <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                                    <label className="block text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                                        Color de Etiqueta
                                    </label>
                                    <div className="flex flex-wrap gap-3">
                                        {/* Color Picker Grid */}
                                        {colorOptions.map(color => (
                                            <button
                                                key={color.value}
                                                type="button"
                                                onClick={() => setEditingItem({ ...editingItem, color: color.value })}
                                                className={`w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center ${editingItem.color === color.value
                                                    ? 'ring-2 ring-offset-2 ring-gray-900 scale-110 shadow-lg'
                                                    : 'hover:scale-110 hover:shadow-md border border-black/5 opacity-90'
                                                    }`}
                                                style={{ backgroundColor: color.value }}
                                                title={color.name}
                                            >
                                                {editingItem.color === color.value && <Check className="w-5 h-5 text-white drop-shadow-md" strokeWidth={3} />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex gap-3 backdrop-blur-sm">
                            <button
                                onClick={handleCancelEdit}
                                className="flex-1 px-4 py-3 border border-gray-200 bg-white text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:shadow-sm transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={!editingItem.name.trim()}
                                className={`flex-1 px-4 py-3 ${colors.bg} text-white font-bold rounded-xl hover:brightness-110 shadow-lg shadow-${accentColor}-500/30 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CatalogManager;
